import { useEffect, useState } from 'react';
import { Activity, AlertTriangle, RefreshCw } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { complianceApi, ApiError } from '@solara/api-client';
import type { ComplianceMetricDto } from '@solara/api-client';

export default function ComplianceDashboardPage() {
  const { t } = useTranslation('p1');
  const [metrics, setMetrics] = useState<ComplianceMetricDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      setMetrics(await complianceApi.dashboard());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  async function runChecks() {
    setBusy(true);
    setError(null);
    try {
      const fresh = await complianceApi.runChecks();
      setMetrics(fresh);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('compliance.dashboardTitle')}</h1>
          <p className="text-fg-muted text-sm">{t('compliance.dashboardSubtitle')}</p>
        </div>
        <Button onClick={runChecks} disabled={busy}>
          <RefreshCw className={`mr-2 h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
          {busy ? t('actions.loading') : t('compliance.runChecks')}
        </Button>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertTriangle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {metrics.map((m) => (
          <Card key={m.checkType} className="rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <Activity className="h-3 w-3" />
                  {t(`compliance.checks.${m.checkType}`, { defaultValue: m.label })}
                </span>
                <StatusBadge status={m.status} t={t} />
              </CardTitle>
            </CardHeader>
            <CardBody className="space-y-1 text-sm">
              <p>
                <span className="text-fg-muted text-xs">{t('compliance.current')}</span>{' '}
                <strong>{m.currentValue}</strong>
              </p>
              <p>
                <span className="text-fg-muted text-xs">{t('compliance.threshold')}</span>{' '}
                <code className="rounded bg-surface-muted px-1 py-0.5 text-xs">{m.thresholdValue}</code>
              </p>
              <p className="text-[10px] text-fg-muted">
                {t('compliance.lastComputed', { when: new Date(m.computedAt).toLocaleString() })}
              </p>
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({
  status,
  t,
}: {
  status: ComplianceMetricDto['status'];
  t: (k: string, vars?: Record<string, string | number>) => string;
}) {
  const tone: 'success' | 'warning' | 'danger' =
    status === 'OK' ? 'success' : status === 'WARNING' ? 'warning' : 'danger';
  return (
    <Badge tone={tone} className="text-[10px]">
      {t(`compliance.statuses.${status}`, { defaultValue: status })}
    </Badge>
  );
}
